import { i18n } from "i18next";

import { translateApplicationSourceType } from "../../utils/applicationSourceType";

import type { FilterSectionConfig } from "@/components/ui/v2/filters/FiltersDrawer";
import type { ApplyFilterOption, ApplyFiltersData } from "@/features/integrations/modules/applies/api";

export type FilterKey = keyof ApplyFiltersData;

const FILTER_KEY_TO_PARAM: Record<FilterKey, string> = {
  client: "clients",
  integration: "integrations",
  applicationSourceType: "applicationSourceTypes",
  publicationId: "publicationIds",
  matchId: "matchIds",
  candidateId: "candidateIds",
};

/**
 * Dimensions whose option count grows with the applies rather than with a catalogue — roughly one
 * distinct value per apply, so a default date range yields tens of thousands. They render as async
 * sections: searched server-side, loaded a page at a time on scroll. Everything else (client,
 * integration) arrives complete and is filtered in the browser.
 */
const ASYNC_FILTER_KEYS: readonly FilterKey[] = ["publicationId", "matchId", "candidateId"];

export function isAsyncFilterKey(key: string): boolean {
  return (ASYNC_FILTER_KEYS as readonly string[]).includes(key);
}

/** Build active filter params from URL search params. Returns undefined for empty arrays. */
export function buildAppliesFilterParams(
  searchParams: URLSearchParams,
  filterKeys: readonly FilterKey[],
) {
  const result: Record<string, string[] | undefined> = {};
  for (const key of filterKeys) {
    const values = searchParams.getAll(key);
    result[FILTER_KEY_TO_PARAM[key]] = values.length ? values : undefined;
  }
  return {
    clients: result.clients,
    integrations: result.integrations,
    applicationSourceTypes: result.applicationSourceTypes,
    publicationIds: result.publicationIds,
    matchIds: result.matchIds,
    candidateIds: result.candidateIds,
  };
}

function getOptionLabel(key: FilterKey, option: ApplyFilterOption, t: i18n["t"]): string {
  if (key === "applicationSourceType") return translateApplicationSourceType(option.value, t);
  return t(option.label, { defaultValue: option.label });
}

export function getFilterSections<K extends FilterKey>(
  filters: Pick<ApplyFiltersData, K> | undefined,
  filterKeys: readonly K[],
  t: i18n["t"],
): FilterSectionConfig[] {
  if (!filters) return [];
  return filterKeys
    .filter(
      // An async section's options are unknown until the user expands it, so it is always offered;
      // a complete dimension is dropped when it has nothing to offer.
      (key) => isAsyncFilterKey(key) || (filters[key]?.length ?? 0) > 0,
    )
    .map((key) =>
      isAsyncFilterKey(key)
        ? { key, title: `common.${key}`, type: "async" as const }
        : {
          key,
          title: `common.${key}`,
          options: filters[key].map((o) => ({
            ...o,
            label: getOptionLabel(key, o, t),
          })),
        },
    );
}

export function getValueToLabel<K extends FilterKey>(
  filters: Pick<ApplyFiltersData, K> | undefined,
  filterKeys: readonly K[],
  t: i18n["t"],
): Record<K, Record<string, string>> {
  const map = Object.fromEntries(filterKeys.map((k) => [k, {}])) as Record<
    K,
    Record<string, string>
  >;
  if (!filters) return map;
  for (const key of filterKeys) {
    // Async dimensions are deliberately absent: only their first page is loaded here, and their label
    // is the value itself, so the chips' `?? value` fallback already renders them correctly.
    if (isAsyncFilterKey(key)) continue;
    const labels: Record<string, string> = map[key];
    filters[key]?.forEach((o) => {
      labels[o.value] = getOptionLabel(key, o, t);
    });
  }
  return map;
}
